import { useState, useEffect } from "react";
import API from "@/lib/api";
import { CalendarDays, ChevronLeft, ChevronRight } from "lucide-react";

export const STATUS_META = {
  on_track: { label: "On track", dot: "bg-[#39FF14]", text: "text-[#39FF14]", bg: "bg-[#39FF14]/10" },
  building: { label: "Building", dot: "bg-[#FFE600]", text: "text-[#FFE600]", bg: "bg-[#FFE600]/10" },
  needs_help: { label: "Needs help", dot: "bg-magenta", text: "text-magenta", bg: "bg-magenta/10" },
};

const fmtWeek = (iso) => new Date(`${iso}T12:00:00`).toLocaleDateString(undefined, { month: "short", day: "numeric" });

export default function WeeklyReport({ classId, reloadKey, endpoint = "/teacher/weekly-report", onSelect, initialOffset = 0 }) {
  const [offset, setOffset] = useState(initialOffset);
  const [report, setReport] = useState(null);

  useEffect(() => {
    let alive = true;
    API.get(endpoint, { params: { week_offset: offset, ...(classId ? { class_id: classId } : {}) } })
      .then(({ data }) => alive && setReport(data))
      .catch(() => alive && setReport({ students: [], summary: {}, goal: 20 }));
    return () => { alive = false; };
  }, [offset, classId, reloadKey, endpoint]);

  return (
    <div className="glass-surface rounded-xl p-6 mb-6" data-testid="weekly-report">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-5">
        <div className="flex items-center gap-2">
          <CalendarDays className="w-5 h-5 text-cyber" />
          <h2 className="font-heading text-lg font-bold text-white">Weekly Progress</h2>
          <span className="font-mono text-xs text-white/40 ml-2" data-testid="weekly-goal-label">Goal: {report?.goal ?? "-"} new facts / week</span>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setOffset((o) => o + 1)} data-testid="weekly-prev-btn" className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 transition-colors duration-200">
            <ChevronLeft className="w-4 h-4 text-white/60" />
          </button>
          <span className="font-mono text-xs text-white/70 w-36 text-center" data-testid="weekly-week-label">
            {offset === 0 ? "This week" : offset === 1 ? "Last week" : `Week of ${report ? fmtWeek(report.week_start) : ""}`}
            {report && offset < 2 && <span className="text-white/30"> · {fmtWeek(report.week_start)}</span>}
          </span>
          <button onClick={() => setOffset((o) => Math.max(0, o - 1))} disabled={offset === 0} data-testid="weekly-next-btn" className="p-1.5 rounded-lg bg-white/5 hover:bg-white/10 disabled:opacity-30 transition-colors duration-200">
            <ChevronRight className="w-4 h-4 text-white/60" />
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-3 mb-5">
        {Object.entries(STATUS_META).map(([k, m]) => (
          <span key={k} className={`flex items-center gap-2 px-3 py-1.5 rounded-full font-mono text-xs ${m.bg} ${m.text}`} data-testid={`weekly-summary-${k}`}>
            <span className={`w-2 h-2 rounded-full ${m.dot}`} /> {m.label}: {report?.summary?.[k] ?? 0}
          </span>
        ))}
      </div>

      {!report ? (
        <p className="font-mono text-sm text-white/30 animate-pulse">Loading report...</p>
      ) : report.students.length === 0 ? (
        <p className="font-body text-sm text-white/30 py-4" data-testid="weekly-empty">No students in your classes yet</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[860px]">
            <thead>
              <tr className="border-b border-white/5">
                {["Student", "Facts mastered", "Correct / min", "Avg answer", "Accuracy", "Days", "Tricky facts", "Band progress"].map((h) => (
                  <th key={h} className="text-left p-3 font-mono text-xs text-white/40 uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {report.students.map((s) => <WeeklyRow key={s.id} s={s} onSelect={onSelect} />)}
            </tbody>
          </table>
        </div>
      )}
      <p className="font-body text-xs text-white/30 mt-4">
        A fact is mastered when answered correctly within 5 seconds, 3 times in a row. Green = goal met, yellow = at least half, red = under half.
        Correct/min counts only time spent answering (not games).
      </p>
    </div>
  );
}

function WeeklyRow({ s, onSelect }) {
  const m = STATUS_META[s.status];
  return (
    <tr onClick={onSelect ? () => onSelect(s.id) : undefined}
      className={`border-b border-white/5 last:border-0 hover:bg-white/[0.02] transition-colors duration-200 ${onSelect ? "cursor-pointer" : ""}`} data-testid={`weekly-row-${s.id}`}>
      <td className="p-3">
        <p className="font-body text-sm text-white">{s.name}</p>
        <p className="font-body text-xs text-white/30">{s.email}</p>
      </td>
      <td className="p-3">
        <span className={`inline-flex items-center gap-2 px-2.5 py-1 rounded-full font-mono text-sm font-bold ${m.bg} ${m.text}`} data-testid={`weekly-mastered-${s.id}`}>
          <span className={`w-2 h-2 rounded-full ${m.dot}`} /> {s.facts_mastered} / {s.goal}
        </span>
      </td>
      <td className="p-3 font-mono text-sm text-white">{s.correct_per_minute}</td>
      <td className="p-3 font-mono text-sm text-white/70">{s.correct ? `${s.avg_answer_time}s` : "-"}</td>
      <td className="p-3 font-mono text-sm text-white/70">{s.problems ? `${s.accuracy}%` : "-"}</td>
      <td className="p-3 font-mono text-xs text-white/60">{s.days_practiced.length ? s.days_practiced.join(", ") : "-"}</td>
      <td className="p-3">
        <div className="flex flex-wrap gap-1">
          {s.tricky_facts.length === 0 ? <span className="font-mono text-xs text-white/20">none</span> :
            s.tricky_facts.map((t) => (
              <span key={t.problem} className="font-mono text-xs px-1.5 py-0.5 rounded bg-magenta/10 text-magenta" title={`Missed ${t.misses} times`}>
                {t.problem.replace(/\s*=\s*\?$/, "")}
              </span>
            ))}
        </div>
      </td>
      <td className="p-3 w-40">
        <p className="font-body text-xs text-white/50 mb-1">Band {s.band + 1}: {s.band_percent}%</p>
        <div className="h-1.5 rounded-full bg-white/5 overflow-hidden">
          <div className="h-full bg-cyber" style={{ width: `${s.band_percent}%` }} />
        </div>
      </td>
    </tr>
  );
}
